// Pure helpers unique to the chat-driven Smart Import flow. Everything else (column
// auto-detection, template resolution, employee fuzzy matching, confirm payload,
// etc.) is reused verbatim from components/EmployeeCost/SmartImport — these two
// functions fill the two gaps that flow needs and the modal wizard doesn't have:
// a negative-amount default and an on-upload "best template" suggestion.

import { parseNumber } from 'components/EmployeeCost/SmartImport/parsers';
import type { ColumnMapping } from 'components/EmployeeCost/SmartImport/wizardReducer';
import {
	parseTemplateEntries,
	type MappingTemplate,
} from 'components/EmployeeCost/SmartImport/steps/step2Helpers';

// autoSelectSoleField now lives in step2Helpers.ts (Step 2 uses it too, to
// auto-suggest a field alongside a suggested category) — re-exported here so
// existing imports from this module keep working.
export { autoSelectSoleField } from 'components/EmployeeCost/SmartImport/steps/step2Helpers';

/**
 * Sets `negate: true` by default on every numeric, non-sentinel column that
 * contains at least one negative value anywhere in the file's data rows (not just
 * the 10-sample cache `buildAutoDetectedMappings` keeps).
 *
 * Run AFTER category suggestion (`suggestCategories`/`autoSelectSoleField`) — every
 * non-name column starts out at category 'skip' from `buildAutoDetectedMappings`,
 * and this function deliberately leaves 'skip' columns alone (so it never fights a
 * deliberate Skip choice); negating first would make it a no-op for every column.
 * Also run BEFORE any template is applied — an applied template's own saved
 * `negate` values are authoritative and should win, and since
 * `resolveTemplateToMappings`/`TEMPLATE_APPLIED` replace `columnMappings` wholesale,
 * that precedence falls out for free.
 */
export function detectNegativeDefaults(
	rawMatrix: string[][],
	headerRowIndex: number,
	columnMappings: ColumnMapping[]
): ColumnMapping[] {
	const dataRows = rawMatrix.slice(headerRowIndex + 1);
	return columnMappings.map((m) => {
		if (m.classifier !== 'numeric') return m;
		if (m.category === 'employee_name' || m.category === 'skip') return m;
		const hasNegative = dataRows.some((row) => {
			const cell = row[m.colIndex];
			if (cell === undefined) return false;
			const parsed = parseNumber(String(cell));
			return !Number.isNaN(parsed) && parsed < 0;
		});
		return hasNegative ? { ...m, negate: true } : m;
	});
}

const headerKey = (h: string) => h.trim().toLowerCase();

/** How well one template's saved entries overlap this file's headers. */
export interface TemplateMatch {
	template: MappingTemplate;
	overlapCount: number;
	totalColumns: number;
}

/**
 * Score every saved template by how many of the file's headers it recognizes
 * (case-insensitive, by name — the same key `resolveTemplateToMappings` matches
 * on), and return the best one, provided it covers at least half of the file's
 * columns. Returns null when there are no templates, or none clears the bar.
 */
export function findBestTemplateMatch(
	headers: string[],
	templates: MappingTemplate[]
): TemplateMatch | null {
	if (headers.length === 0 || templates.length === 0) return null;
	const headerKeys = new Set(headers.map(headerKey));

	let best: TemplateMatch | null = null;
	for (const template of templates) {
		const entries = parseTemplateEntries(template.mappings);
		if (entries.length === 0) continue;
		const overlapCount = entries.filter((e) => headerKeys.has(headerKey(e.sourceHeader))).length;
		if (!best || overlapCount > best.overlapCount) {
			best = { template, overlapCount, totalColumns: headers.length };
		}
	}
	if (!best || best.overlapCount === 0) return null;
	if (best.overlapCount / best.totalColumns < 0.5) return null;
	return best;
}
